import { FC, Ref } from 'react'
import { Box, List, Slide, Stack, Typography } from '@mui/material'
import camelcase from 'camelcase'

import { APP_BAR_HEIGHT, NAV_DRAWER_RADIUS_PX, NAV_FLYOUT_WIDTH_PX } from '@/common/constants'
import { navDrawerTransition } from '@/components/layouts/CurbsNav/navDrawer'
import { PageItem } from '@/components/layouts/CurbsNav/types'
import CurbsNavDrawerItem from '@/components/layouts/CurbsNavDrawerItem'

export const NAV_FLYOUT_ID = 'curb-nav-flyout'

interface CurbsNavFlyoutProps {
  open: boolean
  /** The group whose sub-pages to show. Kept after closing, so they stay while it slides out. */
  group?: PageItem
  /** The drawer's current width in pixels. The sub-pages start at its edge. */
  drawerWidth: number
  /** Whether the drawer is open, to match its animation when the flyout follows its edge. */
  drawerOpen: boolean
  panelRef?: Ref<HTMLElement>
}

/**
 * The white panel beside the nav drawer that holds a group's sub-pages.
 *
 * It floats over the page instead of pushing it, so opening it does not change
 * the page width, and it opens the same way when the drawer is collapsed.
 * `CurbsNav` owns when it opens and closes.
 */
const CurbsNavFlyout: FC<CurbsNavFlyoutProps> = ({
  open,
  group,
  drawerWidth,
  drawerOpen,
  panelRef,
}) => {
  if (!group?.children) return null

  return (
    /*
      Under the drawer paper, so the panel slides out from behind it, but over
      the app bar and the map overlays.

      The fixed box is a separate element from the panel because `Slide` writes
      an inline `transition` on its child, which would replace any transition
      set there.
    */
    <Box sx={theme => ({ position: 'fixed', top: 0, left: 0, zIndex: theme.zIndex.drawer - 1 })}>
      <Slide
        direction="right"
        in={open}
        mountOnEnter
        unmountOnExit
        // Moves focus into the panel, so a keyboard user does not have to tab
        // through the rest of the drawer to reach the sub-pages.
        onEntering={node => {
          node.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true })
        }}
      >
        {/*
          The panel starts at the screen's left edge, not the drawer's right
          edge, and runs behind the whole drawer. The drawer's rounded corner
          cuts into its paper, and the panel is what shows through the cut.
          Starting at the drawer's edge would leave the grey app bar strip
          showing there instead.
        */}
        <Box
          ref={panelRef}
          component="nav"
          id={NAV_FLYOUT_ID}
          aria-label={group.title}
          data-testid={NAV_FLYOUT_ID}
          sx={theme => ({
            display: 'flex',
            height: '100vh',
            backgroundColor: 'common.white',
            color: 'text.primary',
            borderRadius: `0 ${NAV_DRAWER_RADIUS_PX}px 0 0`,
            boxShadow: theme.shadows[8],
          })}
        >
          {/* Covered by the drawer. Follows its width as it opens and closes. */}
          <Box
            aria-hidden
            sx={theme => ({
              width: `${drawerWidth}px`,
              flexShrink: 0,
              transition: navDrawerTransition(theme, drawerOpen, 'width'),
            })}
          />
          <Box sx={{ width: `${NAV_FLYOUT_WIDTH_PX}px`, overflowY: 'auto' }}>
            {/* Same height as the drawer's logo row, so the two lists start level. */}
            <Stack justifyContent="center" sx={{ minHeight: APP_BAR_HEIGHT, px: 3.5 }}>
              <Typography component="h2" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
                {group.title}
              </Typography>
            </Stack>
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {group.children.map(child => (
                <CurbsNavDrawerItem
                  nested
                  // The flyout always shows labels, whatever the drawer's state.
                  drawerOpen
                  key={child.title}
                  testId={child.testId ?? camelcase(child.title)}
                  title={child.title}
                  disabled={child.disabled}
                  isActive={child.isActive}
                  to={child.link}
                />
              ))}
            </List>
          </Box>
        </Box>
      </Slide>
    </Box>
  )
}

export default CurbsNavFlyout
